import { useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { useRedirectIfSignedIn } from 'web/hooks/use-redirect-if-signed-in'
import { signUpWithEmail, loginWithGoogle } from 'web/lib/supabase/auth'

export default function SignupPage() {
  const { t } = useTranslation()
  const router = useRouter()
  useRedirectIfSignedIn()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError(t('resetPassword.errorMinLength'))
      return
    }

    setLoading(true)
    try {
      await signUpWithEmail(email, password, { full_name: name })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[#fafafa]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link href="/" className="inline-flex items-center gap-2">
            <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="4" fill="#000" />
              <path d="M8 20L14 8L20 20H17L14 14L11 20H8Z" fill="white" />
            </svg>
            <span className="text-2xl font-bold text-black">
              {t('brand.name')}
            </span>
          </Link>
        </div>

        <div
          className="rounded-xl bg-white p-8"
          style={{ border: '1px solid #e8e8e8' }}
        >
          <h1 className="mb-6 text-center text-2xl font-bold text-gray-900">
            {t('auth.signUp')}
          </h1>

          {success ? (
            <div className="rounded-lg bg-green-50 p-4 text-center text-green-800">
              <p className="font-medium">{t('forgotPassword.successTitle')}</p>
              <p className="mt-1 text-sm">
                {t('auth.emailConfirmed') === 'Email confirmado!'
                  ? 'Verifique seu email para confirmar sua conta.'
                  : 'Check your email to confirm your account.'}
              </p>
            </div>
          ) : (
            <>
              <button
                onClick={() => loginWithGoogle()}
                className="mb-4 flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 text-[15px] font-medium text-gray-700 transition hover:bg-gray-50"
              >
                <svg viewBox="0 0 48 48" className="h-5 w-5">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
                {t('auth.continueWithGoogle')}
              </button>

              <div className="mb-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-sm text-gray-400">{t('common.or')}</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('profile.displayName')}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={t('onboarding.enterNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('profile.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={t('forgotPassword.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('resetPassword.newPasswordLabel')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? t('common.loading') : t('auth.signUp')}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center text-sm text-gray-500">
            {t('auth.loginTitle') === 'Entrar para prever'
              ? 'Ja tem uma conta?'
              : 'Already have an account?'}{' '}
            <Link
              href="/login"
              className="font-medium text-blue-600 hover:underline"
            >
              {t('nav.login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
